/**
 * First-person camera rig — handlebar POV.
 *
 * Stage 1a + Stage 4b cockpit art. A PerspectiveCamera parented to a small
 * rig group that:
 *  - sits at (player.x, cameraHeight, ~0) looking down -Z (the travel dir),
 *  - rolls into strafe: roll = -strafeRollDeg * (vx / lateralMaxSpeed),
 *  - widens FOV with speed: fov = fovBase + fovSpeedBonus * speedFrac (lerped),
 *  - holds the cockpit (handlebar GLB) STATIC vertically (arcade style — the
 *    old vertical bob never matched the ground shadow and was removed),
 *  - spins a procedural front wheel (slim dark tyre + alloy rim + light spokes)
 *    from the sim's own travel DISTANCE so it sells speed and freezes whenever
 *    the sim does, placed so its tyre rides ON the road at every steer/lean
 *    angle (see `frontWheelHubY`); what makes the spin VISIBLE is a shaded
 *    tread pattern, not geometry — see the SPIN CUE note below,
 *  - uses render-interpolation alpha between the previous and current player
 *    x for butter-smooth lateral motion at any display refresh rate.
 *
 * The cockpit GLB (assets/models/cockpit.glb, 0.44 m handlebar + hands) is
 * parented to the camera at the bottom of the frame; its placement (COCKPIT_Y /
 * COCKPIT_Z / COCKPIT_SCALE) puts it a realistic drop below the eye and BEHIND
 * the front hub, which is what keeps the wheel reading as a wheel and not as a
 * black strip. The GLB's grips face +Z (toward
 * the rider/camera) by default; if the generated model's grip orientation
 * differs, Kimi visually verifies and the yaw here is the single tweak point.
 */

import {
  PerspectiveCamera,
  Group,
  Mesh,
  BoxGeometry,
  TorusGeometry,
  CylinderGeometry,
  Vector3,
  type Material,
  type BufferGeometry,
  type Object3D,
} from "three/webgpu";
import { TUNING } from "../sim/tuning";
import type { PlayerState } from "../sim/types";
import { curveTangent } from "./curve";
import {
  plasticMaterial,
  treadBlur,
  treadPhase,
  tyreTreadMaterial,
  type TyreTreadMaterial,
} from "./plastic";

const P = TUNING.player;

const DEG = Math.PI / 180;

// Front wheel parameters (procedural — ASSET_PIPELINE decisions log).
//
// OWNER PLAYTEST (2026-08-02): "The wheel is too thick. Also because it's all
// black we can't really see it spin."
//
// Both notes are the same object read two ways. The tyre was a torus of tube
// radius 0.025 — a 50 mm cross-section on a 600 mm wheel, i.e. a motorcycle
// tyre — and every part of the wheel (rim, spokes, tread) shared ONE near-black
// material. A black torus rotating about its own axis of symmetry is invisible
// rotation BY CONSTRUCTION: every frame is the same picture.
//
// The tyre is now 32 mm across (tube 0.016 — a road tyre is 28 mm; the extra
// 4 mm is the game's chunky-bevel allowance), and the wheel is built from THREE
// tones instead of one: dark tread (carrying the shaded spin pattern — see
// SPIN CUE below), a bright alloy rim nested inside it, and light spokes.
export const WHEEL_RADIUS = 0.30; // m — bike front wheel rim radius (tyre centreline)
const WHEEL_TUBE = 0.016; // m — tyre tube radius (32 mm section)
/** Outer radius — the bit that actually touches the road. */
export const WHEEL_OUTER_RADIUS = WHEEL_RADIUS + WHEEL_TUBE;

/** Alloy rim: a bright ring nested just inside the dark tyre. */
const RIM_RADIUS = 0.2765; // m — centreline; its outer edge tucks under the tyre
const RIM_TUBE = 0.0125; // m
const SPOKE_RADIUS = 0.009; // m — spoke cylinder radius (chunky, but not a bar)
const SPOKE_COUNT = 8; // hub→rim half-spokes (the old 6 were full diameters)

/**
 * SPIN CUE — SHADED, not modelled, and driven by SIM DISTANCE.
 *
 * OWNER REJECTION (2026-08-02): "The wheel spin looks terrible. Rethink how to
 * do it. Also no spin when paused or in lobby."
 *
 * What was there: two coral arcs MODELLED onto the tread, 180° apart, chosen so
 * their 180° period could never land on a multiple of the per-frame rotation.
 * The aliasing analysis was right and the result was still wrong, because the
 * premise was wrong. The wheel spins at ω = v / r, so over the shipped speed
 * range (17.2–44 m/s on r = 0.30 m) it turns 54°–140° PER 60 Hz FRAME. NO
 * discrete geometric marker can read as smooth rotation at that step: avoiding
 * the freeze only buys you a marker that teleports somewhere else each frame.
 * It reads as a colour blinking on the tyre, which is exactly what the owner
 * saw.
 *
 * What replaced it (see `tyreTreadMaterial` in render/plastic.ts):
 *
 *  1. The tread geometry NEVER MOVES — it is a plain torus of revolution, so
 *     rotating it was always invisible anyway. The pattern lives in the
 *     material, shaded from the fragment's own angle about the rolling axis
 *     plus a `phase` uniform. Uniform writes are free and compile nothing.
 *  2. The pattern is LOW FREQUENCY: four broad sectors with soft smoothstep
 *     shoulders (plus a roughness swing, which is half of what carries it on
 *     near-black rubber). No hard edge exists to alias.
 *  3. MOTION BLUR. A `blur` uniform crossfades the sectors to their own mean as
 *     speed rises, reaching a perfectly uniform tyre by 30 m/s — past the one
 *     speed (28.3 m/s) at which a four-sector pattern would freeze. So the
 *     wagon-wheel effect is never on screen: where it would bite, there is no
 *     pattern left to alias. That is also what a real tyre looks like at 44 m/s.
 *  4. PHASE = SIM DISTANCE / RADIUS, not an integrated per-frame delta. The old
 *     code did `wheelSpin += (forwardSpeed / r) * dt` every render frame, and
 *     `forwardSpeed` is `speedStart` on the title screen and holds its last
 *     value while paused — so the wheel span merrily in the lobby and through a
 *     pause. Deriving it from the sim's own travel distance makes a frozen sim a
 *     frozen wheel by construction, in every phase, with no phase checks. The
 *     GEOMETRIC spin (rim + spokes + hub) is derived from the same value.
 *
 * The rim and spokes are not the motion cue; they are the CONTRAST the cue
 * needs (and what makes the thing read as a bicycle wheel rather than a black
 * arc). No material or transparency is touched per frame — every material here
 * is built once in buildWheel and never reassigned.
 */
/**
 * Front hub, camera-local z (m) — how far AHEAD of the eye the wheel is drawn.
 *
 * OWNER PLAYTEST (2026-08-02, after the ground-clearance fix): "the wheel seems
 * way too long from the player POV (it's too far forward)... the arrangement
 * BEFORE was fine, it was just the wheel clipping through the floor."
 *
 * What went wrong. Lifting the hub out of the road (frontWheelHubY, below) moved
 * it 0.43 m UP the camera's local -Y while it was still 1.45 m out in front, and
 * a point that rises while staying that far away climbs the FRAME: the wheel's
 * topmost pixel went from NDC y -0.76 (a stub in the gap between the hands) to
 * -0.43, i.e. a black edge-on strip up the middle of the lower HALF of the
 * screen. Nothing was scaled — it was pure projection.
 *
 * The fix is geometry, not burial. On a real bike the eye is almost over the
 * bars and the front hub is only 0.5-0.8 m ahead of it, so you look STEEPLY
 * DOWN at the tyre and only its crown clears the bottom of your field of view.
 * At WHEEL_Z = -0.85 (hub 0.73 m ahead of the eye once the 7° pitch is undone —
 * `worldZ = -y·sin(pitch) + z·cos(pitch)`) that is exactly what happens, with
 * the contact patch still ON the road.
 *
 * The projection, with the eye's 78° vFOV (NDC y = (y / -z) / tan(39°), both in
 * CAMERA-LOCAL metres — the cockpit is parented to the camera, so its own pitch
 * drops out):
 *
 *   crown (hub + outer radius)  = -0.9359 + 0.3175 = -0.6184  ->  NDC y = -0.898
 *   topmost pixel of the rim    (tangent from the eye to the rim circle:
 *      tan(atan2(hubY, -z) + asin(r / hypot(hubY, z))) / tan(39°))  =  -0.807
 *
 * i.e. the wheel lives in the bottom ~10% of the frame, back to the pre-fix
 * framing (-0.76) and matching the tuning comment that used to sit here.
 * (These moved a hair on 2026-08-02 when the tyre was slimmed from a 50 mm
 * section to 32 mm: a thinner tyre is a smaller outer radius, so the derived hub
 * sits ~8 mm lower and the crown ~2% of the frame lower. The framing rule —
 * bottom sliver, crown NDC ≤ -0.7 — is unchanged and still asserted.)
 */
export const WHEEL_Z = -0.85;

/**
 * Hub height (camera-local metres) that puts the tyre CONTACT PATCH exactly on
 * the road.
 *
 * OWNER BUG (2026-08-02): "the bicycle front wheel appears clipped into the
 * ground". It was: the hub used to be hard-coded at y = -1.28 with the wheel
 * 1.45 m out front, which — with the eye 1.35 m up and pitched 7° down — put
 * the hub 9 cm BELOW the asphalt and buried 42 cm of a 65 cm wheel in it. All
 * the player ever saw was the ~23 cm crown, cut by the road plane into a black
 * stub that grew and shrank as the bike steered. (The comment that used to live
 * here claimed the crown "pokes above the bottom EDGE OF FRAME" — it did, at
 * NDC ≈ -0.81 for the crown / -0.76 for the rim's topmost pixel, and that
 * FRAMING is the one the owner signed off on; see WHEEL_Z, which now reproduces
 * it honestly. What was wrong was the road plane crossing the wheel long before
 * the frame edge did.)
 *
 * The hub is now DERIVED, not guessed: the wheel is placed so its lowest point
 * is the road surface, from the same TUNING numbers that pose the camera. A
 * camera-local point (0, y, z) under the rig's pitch (camera.rotation.x =
 * -pitch) sits at world height
 *
 *     worldY = cameraHeight + y·cos(pitch) + z·sin(pitch)
 *
 * so solving worldY = outerRadius gives the hub height below.
 *
 * LEAN IS SAFE BY CONSTRUCTION. Both the camera's strafe roll and the model's
 * `bikeLean` roll turn about the local Z axis, through a pivot ABOVE the wheel.
 * For a point at (0, y, z) that roll maps y -> y·cos(roll), i.e. it always
 * moves the (negative) hub height TOWARD zero — up. And the wheel's own
 * vertical half-extent is r·cos(roll) once its plane tips. Upright is therefore
 * the worst case for ground clearance in both terms, which is why no
 * compensating lift (and no re-pivoting of `bikeLean` to the contact patch) is
 * needed. Asserted in cameraRig.test.ts across the full lean range.
 */
export function frontWheelHubY(
  wheelZ: number = WHEEL_Z,
  outerRadius: number = WHEEL_OUTER_RADIUS,
  cameraHeight: number = P.cameraHeight,
  pitchDeg: number = P.cameraPitchDeg,
): number {
  const pitch = pitchDeg * DEG;
  return (outerRadius - cameraHeight - wheelZ * Math.sin(pitch)) / Math.cos(pitch);
}

/** Wheel hub height, camera-local (≈ -0.928 m at the shipped tuning). */
const WHEEL_Y = frontWheelHubY();
/**
 * Cockpit GLB placement — the bars/hands, camera-local.
 *
 * The reference LOOK is unchanged and non-negotiable: BIG bars low in frame,
 * spanning most of the frame width, with the arms exiting through the BOTTOM
 * edge so their cut ends are never visible. Model bbox is x ±0.22 m, y ±0.112 m,
 * z ±0.152 m about its origin.
 *
 * The NUMBERS moved with the wheel (2026-08-02). At the old z = -0.95 / y =
 * -0.78 the bars sat 1.04 m ahead of the eye and 0.89 m below it (undo the 7°
 * pitch: `worldY = y·cos(p) + z·sin(p)`, `worldZ = -y·sin(p) + z·cos(p)`) —
 * a reach and a drop no cyclist has, and, now that the front hub is where a
 * front hub belongs (0.73 m ahead), the bars would have been drawn AHEAD OF AND
 * BELOW the front axle, interpenetrating the tyre. The owner's read of the same
 * frame: "maybe the handlebar/hands are too low?".
 *
 * The move is a pure DOLLY of the whole model toward the eye: position AND
 * scale are multiplied by the same k = 0.62 / 0.95 = 0.6526. Scaling an object
 * about the camera's own origin leaves its perspective projection EXACTLY
 * unchanged — every model point keeps its old (x/-z, y/-z) — so the frame the
 * owner signed off on is reproduced pixel for pixel, and only the physical
 * numbers change:
 *
 *   reach   1.04 m ahead of the eye  ->  0.55 m   (hands over the bars)
 *   drop    0.89 m below the eye     ->  0.58 m   (real drop bars: 0.55-0.70)
 *   width   0.81 m of handlebar      ->  0.53 m   (a bar, not a lorry's)
 *   depth   0.95 m ahead of the hub  ->  0.23 m BEHIND it, as on a bicycle
 *
 * Sanity checks on the framing that survive the dolly: the bar half-width still
 * projects to NDC x ≈ ±0.94 (NDC x = (x / -z) / (tan(39°)·aspect), aspect 9:16)
 * and the model's bottom-near corner still lands past NDC y = -1, so the forearm
 * stumps stay hidden below the bottom edge.
 */
const COCKPIT_SCALE = 1.207; // × — bars fill the frame width (NDC x ±0.94)
const COCKPIT_Y = -0.509; // m — 0.58 m below the eye once the 7° pitch is undone
const COCKPIT_Z = -0.62; // m — 0.55 m ahead of the eye; behind the front hub
// --- Bike lean on strafe ---------------------------------------------------
// The camera already rolls P.strafeRollDeg (3°) into a strafe; this is the
// MODEL lean on top of it, so the bars are not rigidly vertical. Roll + a
// touch of steer yaw, both proportional to lateral velocity and smoothed with
// an exponential approach so a flick of input does not snap the bike.
const LEAN_ROLL_DEG = 8; // ° — peak model roll about Z at full lateral speed
// Steer yaw must actually READ on screen: the bars swing 11° and the front
// wheel 1.9× that (~21°), which is how a real bike looks — the wheel leads the
// bars into the turn. The extra 10° lives on a dedicated `wheelSteer` group
// that pivots at the hub, so the wheel's rolling spin (rotation.x on a child)
// composes AFTER the yaw and stays in the wheel's own plane. (Raised from
// 8°/1.5× — at that scale the steer input barely registered in the cockpit.)
const LEAN_STEER_DEG = 11; // ° — peak steer yaw about Y at full lateral speed (bars)
const WHEEL_STEER_MULT = 1.9; // × — front wheel steers harder than the bars
const LEAN_RATE = 10; // 1/s — exp-approach rate toward the target lean
// Cockpit grip orientation: 180° — the generated GLB's grips face the FRONT
// of the bike by default (hands faced the camera, looking like a cyclist
// riding toward us); rotate so knuckles/hoods point down-road.
const COCKPIT_YAW = Math.PI;

/**
 * FP camera rig. Owns the camera + cockpit + procedural front wheel. Call
 * `update` each render frame with the previous and current sim player states
 * plus the interpolation alpha. Call `attachCockpit` after assets load to
 * swap the placeholder for the real GLB (the placeholder is used until then
 * so the rig is never empty during boot).
 */
export class CameraRig {
  readonly camera: PerspectiveCamera;
  /** Rig root added to the scene; camera + cockpit + wheel are children. */
  readonly root: Group;

  private prevX = 0;
  private currentX = 0;
  /** Prev/current lateral velocity — interpolated like x so the strafe roll
   *  doesn't step at the 60 Hz sim rate on faster displays. */
  private prevVx = 0;
  private vx = 0;
  private forwardSpeed: number = P.speedStart;
  /** Smoothed FOV (deg) — lerped toward the target each frame. */
  private fovSmoothed: number = P.fovBase;

  /** Cockpit group (GLB or placeholder), parented to `bikeLean`. Reassigned by attachCockpit. */
  private cockpit: Group;
  /** Procedural front wheel root. Child of `wheelSteer`, at that group's
   *  origin. This group does NOT rotate — the tyre hangs off it directly and
   *  takes its motion from the tread material's phase uniform (see SPIN CUE). */
  private readonly wheel: Group;
  /** The parts that DO turn geometrically (rim, spokes, hub): rotation.x is a
   *  pure roll about the (already steered) hub axis. */
  private wheelRoll!: Group;
  /** Front tyre material — takes the tread phase + motion blur each frame. */
  private treadMat!: TyreTreadMaterial;
  /** Front-wheel steer pivot at the hub: yaws about Y so the wheel visibly
   *  turns into a strafe, on top of the bikeLean roll + bar steer. */
  private readonly wheelSteer: Group;
  /** Bike-lean pivot: parents cockpit + wheel so they roll/steer as one rigid
   *  assembly on strafe, independent of the camera's own roll. */
  private readonly bikeLean: Group;
  /**
   * Ground-level twin of `bikeLean`, hung off the rig ROOT (not the camera):
   * carries exactly the same roll (rotation.z) and steer (rotation.y) angles,
   * but pivots at y = 0 — the tyre contact patch — so anything parented here
   * tips about the ground like a real bike instead of swinging about the eye.
   * The rider shadow caster proxy (render/riderCaster.ts) hangs off this, which
   * is what makes the cast shadow lean and steer with the bike.
   */
  private readonly leanFollowerGroup: Group;
  /** Smoothed lean input, -1 (full left) .. +1 (full right). */
  private leanSmoothedValue = 0;
  /** Long-lived resources (procedural front wheel) — freed only by dispose().
   *  MUST NOT include the placeholder bars: attachCockpit drains those, and
   *  the wheel stays in the scene long after the cockpit swap. */
  private readonly disposables: { geo: BufferGeometry; mat: Material }[] = [];
  /** Placeholder-bar resources — freed by attachCockpit (when the bars leave
   *  the scene) or by dispose() if the swap never happens. */
  private readonly placeholderDisposables: { geo: BufferGeometry; mat: Material }[] = [];
  /** Crash-cam override: when true, update() parks the FP rig at the world
   *  origin and skips its own camera pose so an external driver (the crash
   *  sequence) can place the camera in world space. Additive — false keeps
   *  the existing FP behavior untouched. */
  private crashCamActive = false;

  constructor(aspect: number) {
    this.root = new Group();
    this.root.name = "cameraRig";

    this.camera = new PerspectiveCamera(P.fovBase, aspect, 0.1, 400);
    this.camera.position.set(0, P.cameraHeight, 0);
    this.camera.lookAt(0, P.cameraHeight * 0.6, -10);
    this.root.add(this.camera);

    // --- Bike-lean pivot (bars + wheel roll/steer together on strafe) ---
    // Pivots at the camera origin — the rider's eye — so the whole bike tips
    // as one piece. Children keep their own local positions.
    this.bikeLean = new Group();
    this.bikeLean.name = "bikeLean";
    this.camera.add(this.bikeLean);

    // --- Cockpit (placeholder until attachCockpit swaps in the GLB) -----
    this.cockpit = this.buildPlaceholderBars();
    this.cockpit.position.set(0, COCKPIT_Y, COCKPIT_Z);
    this.cockpit.scale.setScalar(COCKPIT_SCALE);
    this.bikeLean.add(this.cockpit);

    // --- Procedural front wheel (torus rim + cylinder spokes) ----------
    // Nesting: bikeLean > wheelSteer (yaw at the hub) > wheel (roll spin).
    // The spin therefore happens about the wheel's LOCAL rolling axis after
    // the steer yaw, so a steered wheel still rolls in its own plane instead
    // of scything sideways.
    this.wheelSteer = new Group();
    this.wheelSteer.name = "frontWheelSteer";
    this.wheelSteer.position.set(0, WHEEL_Y, WHEEL_Z);
    this.bikeLean.add(this.wheelSteer);

    this.wheel = this.buildWheel();
    this.wheelSteer.add(this.wheel);

    // --- Ground-level lean follower (shadow caster attach point) --------
    // Sits at the rig root's origin (y = 0, the road surface) so its roll
    // pivots at the tyres. update() writes the SAME angles as bikeLean.
    this.leanFollowerGroup = new Group();
    this.leanFollowerGroup.name = "leanFollower";
    this.root.add(this.leanFollowerGroup);
  }

  /**
   * Attach point for anything that must lean/steer with the bike in WORLD-ish
   * space (i.e. not parented to the camera): a group at the rig root's origin,
   * on the ground (y = 0), rotated every frame with exactly the bike-lean roll
   * and steer angles. Used by RiderCaster so the real cast shadow tips with
   * the bike.
   */
  get leanFollower(): Group {
    return this.leanFollowerGroup;
  }

  /** Smoothed lean input, -1 (full left strafe) .. +1 (full right strafe). */
  get leanSmoothed(): number {
    return this.leanSmoothedValue;
  }

  /** Peak bike roll (radians) reached at full lateral speed. */
  get leanRollRad(): number {
    return LEAN_ROLL_DEG * DEG;
  }

  /** Peak bike steer yaw (radians) reached at full lateral speed. */
  get leanSteerRad(): number {
    return LEAN_STEER_DEG * DEG;
  }
  /**
   * Toggle the crash-camera override. When enabled, the FP rig parks its
   * root at the world origin and stops writing the camera's pose, so an
   * external driver (the crash sequence) can place `camera` directly in
   * world space. When disabled, the rig resumes its normal FP behavior on
   * the next `update`. Additive: false is the default and changes nothing.
   *
   * The pose passed when enabling is applied immediately so the camera NEVER
   * sits inside the crash vehicle for even one frame — the cut is instant.
   */
  setCrashCam(enabled: boolean, pose?: { position: Vector3; target: Vector3 }): void {
    this.crashCamActive = enabled;
    if (!enabled) {
      // Restore the FIRST-PERSON eye. The crash path overwrites
      // camera.position with a world-space cinematic pose (metres to the side
      // and BEHIND the rider); the FP path only ever writes the camera's
      // ROTATION and the rig ROOT's position, so without this the eye stayed
      // parked at the crash pose for every subsequent run. Owner bug
      // (2026-07-31): "on subsequent runs the shadow is at a completely off
      // position, not close to the cyclist at all" — the shadow (a child of
      // the rig root) was where it always is; the camera was 3.8 m off to the
      // side and 4 m back, so nothing in the cockpit lined up.
      this.camera.position.set(0, P.cameraHeight, 0);
    }
    // Hide the FP cockpit art (bars + front wheel) while the 3rd-person
    // crash cam owns the camera — they're parented to the camera and would
    // otherwise float in the crash-cam view. Restored when FP control resumes.
    this.cockpit.visible = !enabled;
    this.wheel.visible = !enabled;
    if (enabled && pose) {
      // Park the rig root at the origin so camera.position/lookAt act in
      // world space (root has identity transform → local == world).
      this.root.position.set(0, 0, 0);
      this.root.rotation.set(0, 0, 0);
      this.root.updateMatrixWorld();
      this.camera.position.copy(pose.position);
      this.camera.lookAt(pose.target);
    }
  }

  /** Whether the crash-camera override currently owns the camera. */
  get crashCamEnabled(): boolean {
    return this.crashCamActive;
  }

  /**
   * Swap the placeholder bars for the real cockpit GLB. Call after the asset
   * manager has loaded. Mirrors the placeholder's static placement.
   * The GLB is wrapped in a group so the yaw/orientation tweak is isolated.
   */
  attachCockpit(cockpitGlb: Group): void {
    // Remove the placeholder.
    this.bikeLean.remove(this.cockpit as unknown as Object3D);
    // Dispose the placeholder geometry/materials ONLY — the procedural front
    // wheel is still parented under wheelSteer and rendered every frame, so
    // its geo/mat must survive the cockpit swap (they live in `disposables`).
    for (const d of this.placeholderDisposables) {
      d.geo.dispose();
      d.mat.dispose();
    }
    this.placeholderDisposables.length = 0;

    // Wrap the GLB so we can apply the grip-yaw without touching the source.
    const wrap = new Group();
    wrap.name = "cockpit";
    cockpitGlb.rotation.y = COCKPIT_YAW;
    wrap.add(cockpitGlb);
    wrap.position.set(0, COCKPIT_Y, COCKPIT_Z);
    wrap.scale.setScalar(COCKPIT_SCALE);
    // Replace the cockpit reference; update() holds `this.cockpit` at COCKPIT_Y.
    this.cockpit = wrap;
    this.bikeLean.add(wrap);
  }

  /**
   * Advance the rig's sim-side state from one fixed step to the next.
   * Call this once per sim tick (60 Hz) with the post-step player state.
   * Stores prev/current x for render interpolation.
   */
  step(prevPlayer: PlayerState, current: PlayerState): void {
    this.prevX = prevPlayer.x;
    this.currentX = current.x;
    this.prevVx = prevPlayer.vx;
    this.vx = current.vx;
    this.forwardSpeed = current.forwardSpeed;
  }

  /**
   * Render-frame update: positions the camera using interpolation alpha
   * between the previous and current sim ticks, applies the curve yaw (so the
   * cockpit looks along the bending road), roll, pitch, lerps FOV, holds the
   * cockpit at a fixed height, and spins the front wheel. Call every rAF.
   *
   * @param alpha     interpolation fraction 0..1 between prev and current tick.
   * @param dt        render-frame delta seconds (wheel spin / FOV lerp rate).
   * @param distance  interpolated player travel distance — drives the curve
   *                  yaw. The crash-cam path ignores it (absolute posing).
   */
  update(alpha: number, dt: number, distance: number): void {
    // --- Crash-camera override: an external driver (the crash sequence)
    //     owns the camera pose. Keep the rig root parked at the origin so
    //     that driver's world-space camera.position/lookAt stay correct, and
    //     only tick the cosmetic wheel spin (off-frame, free).
    //     The crash camera poses absolutely — it does NOT inherit the curve
    //     yaw (the cut is a 3rd-person world-space shot, not a cockpit view).
    if (this.crashCamActive) {
      this.root.position.set(0, 0, 0);
      this.root.rotation.set(0, 0, 0);
      this.cockpit.position.y = COCKPIT_Y;
      this.applyWheelSpin(distance);
      // The crash cam poses position/lookAt only — the rig still OWNS the FOV.
      // Re-base it every frame exactly like the FP path so the additive juice
      // layer (FovEffects) always sits on a stable base instead of compounding
      // on its own previous write.
      this.camera.fov = this.fovSmoothed;
      this.camera.updateProjectionMatrix();
      return;
    }

    // --- Lateral position: interpolate prev→current for smoothness -------
    const x = this.prevX + (this.currentX - this.prevX) * alpha;
    this.root.position.set(x, 0, 0);
    // Re-assert the FP eye every frame. setCrashCam(false) already restores it,
    // but the crash cinematic writes camera.position directly each frame, so
    // any path that stops driving it without going through setCrashCam would
    // otherwise leave the eye stranded at the cinematic pose (see the comment
    // in setCrashCam). Rewriting a Vector3 is free next to a projection update.
    this.camera.position.set(0, P.cameraHeight, 0);

    // --- Camera orientation: curve yaw + strafe roll + downward pitch ----
    // The three Euler components are written independently each frame (order
    // XYZ, three's default). Yaw = -atan(tangent(D)) so the cockpit looks
    // along the centerline tangent at the player's road-distance; below 150 m
    // (or on a straight section) the tangent is 0 and this is a pure -Z gaze,
    // leaving the existing roll/pitch behaviour untouched.
    this.camera.rotation.y = -Math.atan(curveTangent(distance));
    // Interpolated lateral velocity: raw sim vx is 60 Hz-stepped; on a
    // 120 Hz+ display the roll would hold-then-jump at tick frequency.
    const vx = this.prevVx + (this.vx - this.prevVx) * alpha;
    const speedFrac = vx / P.lateralMaxSpeed;
    this.camera.rotation.z = -P.strafeRollDeg * DEG * speedFrac;
    // Downward pitch from tuning (portrait framing: more road, less sky).
    this.camera.rotation.x = -P.cameraPitchDeg * DEG;

    // --- FOV speed scaling (smoothed) ------------------------------------
    const speedProgress =
      (this.forwardSpeed - P.speedStart) / (P.speedMax - P.speedStart);
    const targetFov =
      P.fovBase + P.fovSpeedBonus * Math.max(0, Math.min(1, speedProgress));
    const fovK = 4.0; // 1/s — gentle
    this.fovSmoothed += (targetFov - this.fovSmoothed) * (1 - Math.exp(-fovK * dt));
    this.camera.fov = this.fovSmoothed;
    this.camera.updateProjectionMatrix();

    // --- Cockpit height: STATIC ------------------------------------------
    // The bars used to bob vertically (handlebarBobHz/Amp). Owner call: arcade
    // games hold the cockpit rock-steady, and the bob never matched the ground
    // shadow, so it reads as a glitch. Only the front-wheel SPIN sells speed.
    this.cockpit.position.y = COCKPIT_Y;

    // --- Front wheel spin (derived from sim distance — see SPIN CUE) -----
    this.applyWheelSpin(distance);

    // --- Bike lean: roll + steer the MODEL into the strafe ----------------
    // Smoothed exponential approach so the bike settles instead of snapping.
    // Signs match the camera's roll convention: strafing right (vx > 0) rolls
    // the assembly right (negative rotation.z) and steers right (negative
    // rotation.y). This stacks on the camera's own P.strafeRollDeg roll.
    const leanTarget = Math.max(-1, Math.min(1, speedFrac));
    this.leanSmoothedValue +=
      (leanTarget - this.leanSmoothedValue) * (1 - Math.exp(-LEAN_RATE * dt));
    const roll = -LEAN_ROLL_DEG * DEG * this.leanSmoothedValue;
    const steer = -LEAN_STEER_DEG * DEG * this.leanSmoothedValue;
    this.bikeLean.rotation.z = roll;
    this.bikeLean.rotation.y = steer;
    // Ground-level twin: the shadow caster proxy hangs off this, so the REAL
    // cast shadow rolls + steers with the bike (pivoting at the tyres, y = 0)
    // instead of staying bolt upright while the cockpit tips.
    this.leanFollowerGroup.rotation.z = roll;
    this.leanFollowerGroup.rotation.y = steer;
    // The wheel steers WHEEL_STEER_MULT× the bars. bikeLean already applied
    // one LEAN_STEER_DEG to both, so the hub pivot adds only the surplus.
    this.wheelSteer.rotation.y =
      -LEAN_STEER_DEG * (WHEEL_STEER_MULT - 1) * DEG * this.leanSmoothedValue;
  }

  /**
   * Write the front wheel's spin for a travel distance: the geometric roll of
   * the rim/spokes/hub, and the tread material's pattern phase + motion blur.
   *
   * DISTANCE, never an integrated `speed * dt`. The sim's distance is frozen on
   * the title screen, while paused and after a game over, so this is what makes
   * the wheel freeze there too — the owner's "no spin when paused or in lobby",
   * fixed by construction rather than by phase checks. (The rider shadow's
   * pedalling crank, render/riderCaster.ts, already gates on `playing` and so
   * already froze; the wheel was the one thing still turning.)
   *
   * The phase is wrapped to [0, 2π): a run reaches tens of thousands of metres,
   * and the phase uniform is a float32. Wrapping is invisible on the geometry
   * too — a whole turn of the wheel is the identity.
   *
   * SIGN. `rotation.x = -phase` rolls the top of the wheel toward -Z, i.e.
   * forward. The tread pattern is shaded at `theta + phase` in the tyre's own
   * (never-rotated) local frame, which advances the sectors the same way; see
   * `tyreTreadMaterial`.
   */
  private applyWheelSpin(distance: number): void {
    const phase = treadPhase(distance, WHEEL_RADIUS);
    this.wheelRoll.rotation.x = -phase;
    this.treadMat.setSpin(phase, treadBlur(this.forwardSpeed));
  }

  /** Update aspect on resize. */
  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Release GPU resources (procedural front wheel + any remaining placeholder
   *  bars; the cockpit GLB is owned by AssetManager). */
  dispose(): void {
    for (const d of this.disposables) {
      d.geo.dispose();
      d.mat.dispose();
    }
    this.disposables.length = 0;
    for (const d of this.placeholderDisposables) {
      d.geo.dispose();
      d.mat.dispose();
    }
    this.placeholderDisposables.length = 0;
  }

  // -----------------------------------------------------------------------
  // Procedural front wheel: dark tyre (shaded tread pattern) + alloy rim +
  // light spokes. ASSET_PIPELINE decisions log: procedural, not generated —
  // crisper + can spin. See the WHEEL_* constants and the SPIN CUE note for
  // the dimensions and for why the spin cue is shaded rather than modelled.
  //
  // ORIENTATION (shared by every part). We want the wheel to spin about X
  // (rolling forward), i.e. the wheel disc lies in the Y-Z plane, normal +X.
  // A three TorusGeometry lies in X-Y with normal +Z, so `rotateY(90°)` (which
  // maps Z→X) is what stands it up. A CylinderGeometry's axis is +Y, which is
  // already IN that plane, so a spoke only needs its own rotation.x = θ to
  // point it around the wheel.
  //
  // Every material below is built ONCE here and never touched again — nothing
  // in update() reassigns a material or flips `transparent`, which would cost a
  // pipeline recompile mid-run. They all pass `curve: false`: the cockpit rides
  // WITH the camera and must not take the world bend (render/earthCurve.ts).
  // -----------------------------------------------------------------------
  private buildWheel(): Group {
    const g = new Group();
    g.name = "frontWheel";
    // The parts that turn geometrically. The TYRE is deliberately NOT among
    // them: it is a torus of revolution, so rotating it draws the identical
    // picture every frame, and its visible motion comes from the tread
    // material's phase uniform instead (see the SPIN CUE note).
    const roll = new Group();
    roll.name = "frontWheelRoll";
    g.add(roll);
    this.wheelRoll = roll;

    // --- Tyre: the ring that touches the road ---------------------------
    // Matte rubber (`tire` params, no clearcoat) carrying the procedural
    // four-sector tread pattern + its motion blur — render/plastic.ts
    // `tyreTreadMaterial`. Its section is 40 radial segments so the shaded
    // sector shoulders land on a smooth circle rather than a polygon.
    //
    // COLOUR. Dark slate, not the near-black 0x101013 it started as. The
    // owner's original note on this wheel was "because it's all black we can't
    // really see it spin", and that is a fact about DISPLAY LEVELS, not about
    // the pattern: the tyre is on screen as a ~40 px near-edge-on sliver, and a
    // pattern modulating a colour that lands near black has nowhere to go.
    // 0x3c404b still reads as rubber against the bright toy-plastic world (it
    // is the darkest thing in frame) and gives the tread sectors the range they
    // need. Verified on screen at 4x; see the tone note on TREAD_AMP.
    const tyreMat = tyreTreadMaterial({
      color: 0x3c404b,
      name: "frontTyre",
      tube: WHEEL_TUBE,
    });
    this.treadMat = tyreMat;
    const tyreGeo = new TorusGeometry(WHEEL_RADIUS, WHEEL_TUBE, 10, 40);
    tyreGeo.rotateY(Math.PI / 2); // wheel plane = YZ, normal = X, rolls about X
    g.add(new Mesh(tyreGeo, tyreMat));
    this.disposables.push({ geo: tyreGeo, mat: tyreMat });

    // --- Rim: bright alloy ring nested INSIDE the tyre -------------------
    // Its outer edge (RIM_RADIUS + RIM_TUBE = 0.289) tucks a few millimetres
    // under the tyre's inner edge (WHEEL_RADIUS - WHEEL_TUBE = 0.284) so the
    // two read as seated rather than as two floating hoops. `candy` — an alloy
    // rim wants the clearcoat lobe, and that moving glint is half of why the
    // wheel now reads as turning at all.
    const alloyMat = plasticMaterial({
      color: 0xd7dce6,
      name: "frontRimAlloy",
      kind: "candy",
      curve: false,
    });
    const rimGeo = new TorusGeometry(RIM_RADIUS, RIM_TUBE, 8, 32);
    rimGeo.rotateY(Math.PI / 2);
    roll.add(new Mesh(rimGeo, alloyMat));
    this.disposables.push({ geo: rimGeo, mat: alloyMat });

    // --- Spokes: hub → rim, in the wheel's plane -------------------------
    // HALF-spokes (hub to rim), not the old full diameters: SPOKE_COUNT of
    // them is SPOKE_COUNT arms, so 8 spokes give a 45° pitch instead of the
    // 30° a 6-diameter wheel gave — coarser, chunkier, and less prone to
    // shimmering into a grey haze at speed.
    const spokeMat = plasticMaterial({
      color: 0xb9c0cc,
      name: "frontSpokes",
      kind: "candy",
      curve: false,
    });
    const spokeLen = RIM_RADIUS - 0.004;
    const spokeGeo = new CylinderGeometry(SPOKE_RADIUS, SPOKE_RADIUS, spokeLen, 6);
    // Bake the hub→rim offset into the geometry so all SPOKE_COUNT meshes can
    // share it and differ only by rotation.x.
    spokeGeo.translate(0, spokeLen / 2 + 0.004, 0);
    for (let i = 0; i < SPOKE_COUNT; i++) {
      const spoke = new Mesh(spokeGeo, spokeMat);
      spoke.rotation.x = (i / SPOKE_COUNT) * Math.PI * 2;
      roll.add(spoke);
    }
    this.disposables.push({ geo: spokeGeo, mat: spokeMat });

    // --- Hub: small alloy barrel, so the spokes converge on something ----
    const hubGeo = new CylinderGeometry(0.028, 0.028, 0.05, 10);
    hubGeo.rotateZ(Math.PI / 2); // cylinder axis Y → X (the rolling axis)
    roll.add(new Mesh(hubGeo, alloyMat));
    this.disposables.push({ geo: hubGeo, mat: alloyMat });

    return g;
  }

  // -----------------------------------------------------------------------
  // Placeholder bars (used until attachCockpit swaps in the GLB).
  // -----------------------------------------------------------------------
  private buildPlaceholderBars(): Group {
    const g = new Group();
    g.name = "handlebarPlaceholder";

    const barMat = plasticMaterial({ color: 0x141414, name: "barPlaceholder", curve: false });
    const gripMat = plasticMaterial({ color: 0x222226, name: "gripPlaceholder", curve: false });

    const barGeo = new BoxGeometry(0.62, 0.05, 0.15);
    const bar = new Mesh(barGeo, barMat);
    g.add(bar);
    this.placeholderDisposables.push({ geo: barGeo, mat: barMat });

    const gripGeo = new CylinderGeometry(0.022, 0.022, 0.12, 12);
    const gripL = new Mesh(gripGeo, gripMat);
    gripL.position.set(-0.27, 0.04, 0.0);
    gripL.rotation.x = Math.PI / 2;
    g.add(gripL);
    const gripR = new Mesh(gripGeo, gripMat);
    gripR.position.set(0.27, 0.04, 0.0);
    gripR.rotation.x = Math.PI / 2;
    g.add(gripR);
    this.placeholderDisposables.push({ geo: gripGeo, mat: gripMat });

    return g;
  }
}

